import type { DeployEnvironment } from "@enrein/shared-contracts/deploy-env";
import { normalizeDeployEnvironment } from "@enrein/shared-contracts/deploy-env";

export interface StudioRuntimeConfig {
  readonly apiBaseUrl: string;
  readonly agencyId: string;
  readonly pollIntervalMs: number;
  readonly clientPortalBaseUrl: string;
  readonly deployEnv: DeployEnvironment;
  readonly useDevAgencyHeader: boolean;
  readonly v142NewUiEnabled: boolean;
  readonly studioUiVariant: StudioUiVariant;
  readonly whiteLabelLogoUrl: string | null;
}

export type StudioUiVariant = "v142" | "guided" | "v10" | "greenfield";

export const STUDIO_POLL_DEFAULT_MS = 10_000;
export const STUDIO_POLL_MIN_MS = 5_000;
export const STUDIO_POLL_MAX_MS = 60_000;

interface StudioImportMetaEnv {
  readonly VITE_ENREIN_API_BASE_URL?: string;
  readonly VITE_ENREIN_STUDIO_DEV_AGENCY_ID?: string;
  readonly VITE_ENREIN_STUDIO_POLL_INTERVAL_MS?: string;
  readonly VITE_ENREIN_CLIENT_PORTAL_BASE_URL?: string;
  readonly VITE_ENREIN_DEPLOY_ENV?: string;
  readonly VITE_ENREIN_STUDIO_WHITE_LABEL_LOGO_URL?: string;
  readonly VITE_ENREIN_V142_NEW_UI?: string;
  readonly VITE_ENREIN_STUDIO_UI?: string;
}

function readWhiteLabelLogoUrl(raw: string | undefined): string | null {
  const trimmed = raw?.trim() ?? "";
  if (trimmed.length === 0) return null;
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "https:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

function env(): StudioImportMetaEnv {
  return import.meta.env as StudioImportMetaEnv;
}

export function clampPollingIntervalMs(value: number): number {
  if (!Number.isFinite(value)) return STUDIO_POLL_DEFAULT_MS;
  if (value < STUDIO_POLL_MIN_MS) return STUDIO_POLL_MIN_MS;
  if (value > STUDIO_POLL_MAX_MS) return STUDIO_POLL_MAX_MS;
  return Math.trunc(value);
}

export function readStudioRuntimeConfig(): StudioRuntimeConfig {
  const e = env();
  const apiBaseRaw = e.VITE_ENREIN_API_BASE_URL;
  const apiBaseUrl = apiBaseRaw === undefined || apiBaseRaw.trim() === "" ? "" : apiBaseRaw.replace(/\/$/, "");
  const agencyId = (e.VITE_ENREIN_STUDIO_DEV_AGENCY_ID ?? "").trim();
  const requestedPoll = Number(e.VITE_ENREIN_STUDIO_POLL_INTERVAL_MS ?? STUDIO_POLL_DEFAULT_MS);
  const clientPortalBaseRaw = e.VITE_ENREIN_CLIENT_PORTAL_BASE_URL;
  const clientPortalBaseUrl = clientPortalBaseRaw === undefined || clientPortalBaseRaw.trim() === "" ? "" : clientPortalBaseRaw.replace(/\/$/, "");
  const useDevAgencyHeader = !import.meta.env.PROD && agencyId.length > 0;
  const deployEnv = normalizeDeployEnvironment(e.VITE_ENREIN_DEPLOY_ENV);
  const v142NewUiRaw = (e.VITE_ENREIN_V142_NEW_UI ?? "").trim().toLowerCase();
  const v142NewUiEnabled = v142NewUiRaw !== "0" && v142NewUiRaw !== "false";
  const studioUiRaw = (e.VITE_ENREIN_STUDIO_UI ?? "greenfield").trim().toLowerCase();
  // Greenfield is the post-cutover default; V10 remains rollback via VITE_ENREIN_STUDIO_UI=v10.
  const studioUiVariant: StudioUiVariant =
    studioUiRaw === "guided"
      ? "guided"
      : studioUiRaw === "v142"
        ? "v142"
        : studioUiRaw === "greenfield"
          ? "greenfield"
          : "v10";
  return {
    apiBaseUrl,
    agencyId,
    pollIntervalMs: clampPollingIntervalMs(requestedPoll),
    clientPortalBaseUrl,
    deployEnv,
    useDevAgencyHeader,
    v142NewUiEnabled,
    studioUiVariant,
    whiteLabelLogoUrl: readWhiteLabelLogoUrl(e.VITE_ENREIN_STUDIO_WHITE_LABEL_LOGO_URL),
  };
}
